// Content overrides loader.
//
// Admins can override per-intervention task titles + bodies and theme copy
// from Admin → Content. This module fetches the overrides map once at app
// boot and exposes a synchronous getter the graph builder uses to merge
// overrides on top of the baked-in defaults.
//
// API shape (from /api/content/overrides):
//   {
//     headache_relief: {
//       tasks: [{ index: 3, title?: string, content?: string, inputPrompt?: string }, ...],
//       theme: { t4: { storyIntro: "..." }, t6: { positiveVision: "..." }, ... }
//     },
//     ...
//   }

import axios from "axios";
import logger from "./logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let OVERRIDES = {};
let loadPromise = null;
const subscribers = new Set();

function notify() {
  subscribers.forEach((cb) => {
    try { cb(); } catch (err) { logger.error("content override subscriber failed", err); }
  });
}

export function getContentOverrides() {
  return OVERRIDES;
}

export function getOverrideFor(type) {
  return OVERRIDES[type] || { tasks: [], theme: {} };
}

export function subscribeContentOverrides(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

export async function loadContentOverrides({ force = false } = {}) {
  if (!force && loadPromise) return loadPromise;
  loadPromise = axios
    .get(`${API}/content/overrides`)
    .then((res) => {
      OVERRIDES = res.data || {};
      notify();
      return OVERRIDES;
    })
    .catch((err) => {
      logger.warn("content overrides fetch failed; using defaults", err?.message || err);
      OVERRIDES = {};
      return OVERRIDES;
    });
  return loadPromise;
}

// Manually replace the in-memory cache (used after admin saves so the
// editor reflects the new content instantly without a full reload).
export function setContentOverrideFor(type, override) {
  if (!override || (
    (!override.tasks || override.tasks.length === 0) &&
    (!override.theme || Object.keys(override.theme).length === 0)
  )) {
    delete OVERRIDES[type];
  } else {
    OVERRIDES[type] = override;
  }
  notify();
}

// Deep-merge helper used by the graph builder. Only touches plain
// objects — arrays and primitives in `override` replace whatever's in
// `base`. Returns a new object.
export function deepMerge(base, override) {
  if (override == null) return base;
  if (base == null || typeof base !== "object" || Array.isArray(base)) return override;
  if (typeof override !== "object" || Array.isArray(override)) return override;
  const result = { ...base };
  for (const key of Object.keys(override)) {
    result[key] = deepMerge(base[key], override[key]);
  }
  return result;
}
